// components/EventCard.js

import { Check, Pause, Play, Plus, Users } from 'lucide-react';
import { memo } from 'react';
import { isEventActionOpen } from '../utils/eventCutoff';
import { resolveEventDisplayName } from '../utils/eventDisplayName';
import { formatEventDate, formatEventDateTime } from '../utils/eventDateFormat';
import { GLASS_SURFACES, CARD_SOLID_SURFACE_CLASS } from '../config/glassSurfaces';
import AttendeeStack from './AttendeeStack';
import Button from './ui/Button';
import EventCardImage from './EventCardImage';
import { useNowPlaying } from './lineup/NowPlayingBar';
import { playInBar } from '../lib/spotifyPlayerPool';
import { appLog } from '../lib/appLog';
import {
  EVENT_IMAGE_LAYOUT,
  EVENT_IMAGE_SLOT,
  getEventImageUrl,
} from '../lib/images/eventImageUrl';

function normalizeEventCardVariant(variant) {
  if (variant === 'hero' || variant === 'large' || variant === 'tile') return variant;
  if (import.meta.env.DEV) {
    if (variant == null) {
      console.warn('EventCard: `variant` is required; defaulting to "large".');
    } else {
      console.warn(`EventCard: invalid variant "${String(variant)}"; defaulting to "large".`);
    }
  }
  return 'large';
}

/**
 * Event list / rail card. Layout is chosen by the parent via `variant`, not by venue tier.
 *
 * @param {Object} props
 * @param {'hero' | 'large'} props.variant - Required at call sites. When missing or invalid, dev warns and layout falls back to `large`.
 */
function EventCardInner({
  event,
  onRSVP,
  userStatus,
  readOnly = false,
  variant,
  joinSource = 'unknown',
  cornerLabel = null,
  headliners = null,
}) {
  const cardVariant = normalizeEventCardVariant(variant);
  if (cardVariant === 'tile') {
    return (
      <TileEventCard
        event={event}
        onRSVP={onRSVP}
        userStatus={userStatus}
        joinSource={joinSource}
        headliners={headliners}
      />
    );
  }
  if (cardVariant === 'hero') {
    return (
      <HeroEventCard
        event={event}
        onRSVP={onRSVP}
        userStatus={userStatus}
        readOnly={readOnly}
        joinSource={joinSource}
        cornerLabel={cornerLabel}
        headliners={headliners}
      />
    );
  }
  return (
    <LargeEventCard
      event={event}
      onRSVP={onRSVP}
      userStatus={userStatus}
      readOnly={readOnly}
      joinSource={joinSource}
    />
  );
}

export const EventCard = memo(EventCardInner);
EventCard.displayName = 'EventCard';

/** Neutral placeholder while viewer RSVP status is still loading — never paint as Join. */
function RsvpStatusPlaceholder({ size = 'sm', className = '' }) {
  const sizeClass = size === 'lg' ? 'h-12 min-w-[5.5rem] px-6' : 'h-8 min-w-[4.25rem] px-3';
  return (
    <div
      className={`inline-flex shrink-0 items-center justify-center rounded-btn skeleton-shimmer ${sizeClass} ${className}`}
      aria-hidden
    />
  );
}

function RsvpActionButton({
  size,
  userStatus,
  actionsEnabled,
  joinSource,
  cardVariant,
  eventId,
  onRSVP,
  className = '',
  iconClassName,
}) {
  if (userStatus === undefined) {
    return <RsvpStatusPlaceholder size={size} className={className} />;
  }

  const isGoing = userStatus === 'going';

  return (
    <Button
      type="button"
      size={size}
      variant={isGoing ? 'secondary' : 'primary'}
      onClick={(e) => {
        e.stopPropagation();
        appLog('debug', 'join.tap', { eventId, source: joinSource, variant: cardVariant });
        const newStatus = isGoing ? null : 'going';
        onRSVP?.(eventId, newStatus);
      }}
      disabled={!actionsEnabled}
      className={`${className} ${
        !isGoing ? 'ring-1 ring-white/20 dark:ring-dark-border' : ''
      } ${!actionsEnabled ? 'cursor-not-allowed opacity-50' : ''}`}
      aria-label={isGoing ? 'Going' : 'Join'}
    >
      {isGoing ? (
        <Check size={size === 'lg' ? 20 : 16} className={iconClassName} aria-hidden />
      ) : (
        <Users className={iconClassName} aria-hidden />
      )}
      <span>{isGoing ? 'Going' : 'Join'}</span>
    </Button>
  );
}

// --- HERO CARD (festivals, stadiums) ---
function HeroEventCard({
  event,
  onRSVP,
  userStatus,
  readOnly = false,
  joinSource = 'unknown',
  cornerLabel = null,
  headliners = null,
}) {
  const heroImage = getEventImageUrl(event, EVENT_IMAGE_SLOT.HERO);
  const heroLayout = EVENT_IMAGE_LAYOUT[EVENT_IMAGE_SLOT.HERO];
  const goingCount = event.rsvp_going_count || 0;
  const actionsEnabled = isEventActionOpen(event);
  const displayName = resolveEventDisplayName(event);

  return (
    <div className={`relative w-full rounded-2xl overflow-hidden ${readOnly ? 'mb-0' : 'mb-4'}`}>
      <div className="relative h-64 bg-gradient-to-br from-accent-800 to-accent-950">
        {heroImage && (
          <EventCardImage
            src={heroImage}
            alt={displayName}
            width={heroLayout.width}
            height={heroLayout.height}
            className="w-full h-full object-cover opacity-70"
            placeholderClassName="w-full h-full opacity-70"
          />
        )}

        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />

        {/* A corner badge on the image, styled like the event page's genre ("Past" on an expired group's card). */}
        {cornerLabel && <span className="event-card__corner">{cornerLabel}</span>}

        {/* The tiles' play and Join in the corners (Sam's pick, 2026-10-31), 44 px and Join in words on the bigger card
            (his B, 2026-11-04: "+ Join", then "✓ Going"). */}
        {!readOnly && (
          <>
            <TileJoin
              userStatus={userStatus}
              actionsEnabled={actionsEnabled}
              eventId={event.event_id}
              onRSVP={onRSVP}
              joinSource={joinSource}
              hero
            />
            {headliners?.length > 0 && (
              <TilePlay eventId={event.event_id} headliners={headliners} hero />
            )}
          </>
        )}

        <div className="absolute bottom-0 left-0 right-0 p-4 min-w-0">
          <h3 className="text-xl font-bold text-ink line-clamp-2">{displayName}</h3>
          <p className="text-ink/70 text-sm mt-1 line-clamp-1">
            {event.venues?.name || 'Venue TBA'} · {formatEventDate(event)}
          </p>

          {goingCount > 0 && (
            <AttendeeStack
              attendees={event.attendees || []}
              totalGoing={goingCount}
              variant="hero"
              className="mt-1"
            />
          )}
        </div>
      </div>
    </div>
  );
}

// --- LARGE CARD (default non-hero layout) ---
function LargeEventCard({ event, onRSVP, userStatus, readOnly = false, joinSource = 'unknown' }) {
  const cardImage = getEventImageUrl(event, EVENT_IMAGE_SLOT.CARD);
  const cardLayout = EVENT_IMAGE_LAYOUT[EVENT_IMAGE_SLOT.CARD];
  const goingCount = event.rsvp_going_count || 0;
  const actionsEnabled = isEventActionOpen(event);
  const dateTimeString = formatEventDateTime(event);
  const displayName = resolveEventDisplayName(event);

  return (
    <div
      className={`event-card-surface overflow-hidden rounded-[18px] ${GLASS_SURFACES.cards ? '' : CARD_SOLID_SURFACE_CLASS} ${readOnly ? 'mb-0' : 'mb-2'}`}
    >
      <div className="px-3 pt-3">
        <h3 className="glass-title-emphasis text-lg font-bold text-gray-900 dark:text-ink leading-snug truncate">
          {displayName}
        </h3>
      </div>
      <div className="flex flex-row items-start gap-3 p-3 pt-2">
        <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100 dark:bg-dark-bg">
          {cardImage ? (
            <EventCardImage
              src={cardImage}
              alt={displayName}
              width={cardLayout.width}
              height={cardLayout.height}
              className="w-full h-full object-cover"
              placeholderClassName="w-full h-full"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-dark-muted">🎵</div>
          )}
        </div>
        <div className="flex-1 min-w-0 flex flex-col justify-start overflow-hidden">
          <span className="glass-meta-chip text-gray-600 dark:text-ink/70 text-sm truncate leading-tight">
            {event.venues?.name || 'Venue TBA'}
          </span>
          <span className="glass-meta-chip text-gray-600 dark:text-ink/70 text-sm mt-0.5 truncate leading-tight">
            {dateTimeString}
          </span>
          <AttendeeStack
            attendees={event.attendees || []}
            totalGoing={goingCount}
            variant="large"
            className="mt-0.5 leading-tight"
          />
          {!readOnly && (
            <RsvpActionButton
              size="sm"
              userStatus={userStatus}
              actionsEnabled={actionsEnabled}
              joinSource={joinSource}
              cardVariant="large"
              eventId={event.event_id}
              onRSVP={onRSVP}
              className={`mt-2 w-auto self-start gap-1.5 px-3 ${GLASS_SURFACES.cards ? 'ml-1.5' : ''}`}
              iconClassName="h-3.5 w-3.5"
            />
          )}
        </div>
      </div>
    </div>
  );
}

// --- TILE: the Events feed, two across (Sam's A of three, 2026-10-27), in the event page's Sets tiles' style. The photo
// carries the name, "Nov 7 · Hollywood Bowl" and, when anyone is, "12 going" as a third line; two corner buttons: the
// headliner on Spotify top left (only when Spotify knows them) and Join top right (a + that becomes a ✓). Sam's P4 of
// four: a play button at the bottom right cut the name and venue short. ---
function TileJoin({ userStatus, actionsEnabled, eventId, onRSVP, joinSource, hero = false }) {
  if (userStatus === undefined) return null;
  const isGoing = userStatus === 'going';
  // Join is the primary glass, Going the secondary with a lavender ✓ - the event page's order (Sam's B, 2026-11-04: the
  // purple ✓ outshone Join, so Going looked like the thing to tap).
  const cls = [
    'event-tile__join',
    isGoing ? 'btn-glass-neutral event-tile__join--neutral' : 'btn-glass-accent',
    hero ? 'event-hero__btn event-hero__btn--worded' : '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <button
      type="button"
      className={cls}
      aria-label={isGoing ? 'Going' : 'Join'}
      disabled={!actionsEnabled}
      onClick={(e) => {
        e.stopPropagation();
        appLog('debug', 'join.tap', {
          eventId,
          source: joinSource,
          variant: hero ? 'hero' : 'tile',
        });
        onRSVP?.(eventId, isGoing ? null : 'going');
      }}
    >
      {isGoing ? (
        <Check size={16} strokeWidth={2.6} aria-hidden />
      ) : (
        <Plus size={17} strokeWidth={2.6} aria-hidden />
      )}
      {hero && <span>{isGoing ? 'Going' : 'Join'}</span>}
    </button>
  );
}

/** Plays straight away into the page's now-playing bar - the Sets tiles' button (spotifyPlayerPool). Co-headliners come
 * as a list and play as tabs in the bar, like a b2b. */
function TilePlay({ eventId, headliners, hero = false }) {
  const nowPlaying = useNowPlaying();
  const key = `event:${eventId}`;
  const current = nowPlaying.key === key;
  const playing = current && !nowPlaying.paused;
  return (
    <button
      type="button"
      className={`artist-row__play event-tile__play${current ? ' artist-row__play--current' : ''}${playing ? ' artist-row__play--playing' : ''}${hero ? ' event-hero__btn' : ''}`}
      aria-label={`${playing ? 'Pause' : 'Play'} ${headliners.map((a) => a.name).join(' and ')}`}
      onClick={(e) => {
        e.stopPropagation();
        playInBar(key, headliners, current ? nowPlaying.index : 0);
      }}
    >
      {playing ? (
        <>
          <span className="play-eq" aria-hidden>
            <i />
            <i />
            <i />
          </span>
          <Pause
            className="artist-row__play-pause"
            size={14}
            fill="currentColor"
            strokeWidth={0}
            aria-hidden
          />
        </>
      ) : (
        <Play
          size={hero ? 19 : 14}
          fill="currentColor"
          strokeWidth={0}
          className="ml-[2px]"
          aria-hidden
        />
      )}
    </button>
  );
}

function TileEventCard({ event, onRSVP, userStatus, joinSource, headliners }) {
  const image =
    getEventImageUrl(event, EVENT_IMAGE_SLOT.HERO) ||
    getEventImageUrl(event, EVENT_IMAGE_SLOT.CARD);
  const name = resolveEventDisplayName(event);
  const actionsEnabled = isEventActionOpen(event);
  const goingCount = event.rsvp_going_count || 0;
  const meta = [formatEventDate(event), event.venues?.name].filter(Boolean).join(' · ');
  return (
    <div className="event-tile">
      <div className="event-tile__photo">
        {image ? (
          <EventCardImage
            src={image}
            alt=""
            width={320}
            height={320}
            className="event-tile__img"
            placeholderClassName="event-tile__img"
          />
        ) : (
          <div className="event-tile__img event-tile__img--none" aria-hidden>
            🎵
          </div>
        )}
        <div className="event-tile__over">
          <span className="event-tile__name">{name}</span>
          <span className="event-tile__meta">{meta}</span>
          {goingCount > 0 && (
            <span className="event-tile__meta event-tile__meta--going">{goingCount} going</span>
          )}
        </div>
        <TileJoin
          userStatus={userStatus}
          actionsEnabled={actionsEnabled}
          eventId={event.event_id}
          onRSVP={onRSVP}
          joinSource={joinSource}
        />
        {headliners?.length > 0 && <TilePlay eventId={event.event_id} headliners={headliners} />}
      </div>
    </div>
  );
}
